import React from 'react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Ruler, X } from 'lucide-react';

const MeasurementUI = ({
  measureMode,
  isProcessing,
  measurements = {},
  currentRoomId,
  canMeasure,
  onToggleMeasurement,
  onDeleteMeasurement,
  mode = 'default' // 'default' or 'embedded'
}) => {
  const currentRoomMeasurements = measurements[currentRoomId] || [];
  
  // Different positioning for embedded vs default mode
  const containerClasses = mode === 'embedded' 
    ? "absolute top-24 left-2 z-[100] max-w-sm" // Higher z-index for embedded mode, closer to left edge
    : "absolute top-16 left-2 z-[50] max-w-sm"; // Standard z-index for default mode, closer to left edge

  return (
    <>
      {/* Measurement Toggle Button or NO DEPTH Message */}
      <div className={containerClasses}>
        {!canMeasure ? (
          /* Show NO DEPTH message when NPY file is missing */
          <div className="shadow-lg border backdrop-blur-sm bg-red-50/95 hover:bg-red-50 text-red-700 border-red-300 px-3 py-2 rounded text-sm font-medium">
            <div className="flex items-center">
              <Ruler className="w-4 h-4 mr-2" />
              NO DEPTH
            </div>
            <div className="text-xs text-red-600 mt-1">
              Depth data required for measurements
            </div>
          </div>
        ) : (
          /* Show measurement button when NPY file exists */
          <Button
            variant={measureMode ? "default" : "secondary"}
            size="sm"
            onClick={onToggleMeasurement}
            className={`shadow-lg border backdrop-blur-sm ${
              measureMode 
                ? "bg-blue-600 hover:bg-blue-700 text-white border-blue-500" 
                : "bg-white/90 hover:bg-white text-gray-800 border-gray-300"
            }`}
            title="Toggle measurement mode"
          >
            <Ruler className="w-4 h-4 mr-2" />
            {measureMode ? 'Stop Measuring' : 'Measure'}
          </Button>
        )}
      </div>

      {/* Measurement Processing Overlay */}
      {isProcessing && (
        <div className="absolute inset-0 bg-black/30 flex items-center justify-center z-[200] pointer-events-none">
          <div className="text-white text-center bg-black/70 p-4 rounded-lg backdrop-blur-sm">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white mb-2 mx-auto"></div>
            <p className="text-sm font-medium">Processing measurement...</p>
          </div>
        </div>
      )}

      {/* Measurements List - Only show when in measure mode and has measurements */}
      {measureMode && currentRoomMeasurements.length > 0 && (
        <div className={`absolute ${mode === 'embedded' ? 'top-36' : 'top-28'} left-2 max-w-sm z-[100] ${mode === 'embedded' ? 'max-h-[40vh]' : 'max-h-[50vh]'} overflow-y-auto`}>
          <Card className="bg-white/95 backdrop-blur-sm shadow-lg border border-blue-200">
            <CardContent className="p-3">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-semibold text-blue-800">Measurements</h4>
                <span className="text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded">{currentRoomMeasurements.length} items</span>
              </div>
              
              <div className="space-y-2">
                {currentRoomMeasurements.map((measurement) => (
                  <div
                    key={measurement.id}
                    className="text-xs border rounded p-2 bg-white/50 flex justify-between items-start"
                  >
                    <div className="flex-1">
                      <div className="font-medium text-green-600 mb-1">
                        📏 {measurement.distance} cm
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-[10px] text-gray-600">
                        <div>
                          <div className="font-medium">Point 1:</div>
                          <div>P: {measurement.point1.pitch.toFixed(1)}°</div>
                          <div>Y: {measurement.point1.yaw.toFixed(1)}°</div>
                        </div>
                        <div>
                          <div className="font-medium">Point 2:</div>
                          <div>P: {measurement.point2.pitch.toFixed(1)}°</div>
                          <div>Y: {measurement.point2.yaw.toFixed(1)}°</div>
                        </div>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 w-6 p-0 ml-2"
                      onClick={() => onDeleteMeasurement(currentRoomId, measurement.id)}
                    >
                      <X className="w-3 h-3" />
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Measurement Instructions - Show when in measure mode */}
      {measureMode && (
        <div className={`absolute left-2 right-4 z-[80] pointer-events-none ${mode === 'embedded' ? 'bottom-20' : 'bottom-4'}`}>
          <Card className="bg-blue-600/95 text-white backdrop-blur-sm shadow-lg border-2 border-blue-400">
            <CardContent className="p-3 text-center">
              <p className="text-sm font-semibold mb-1">🎯 Measurement Mode Active</p>
              <p className="text-xs font-medium">
                Right-click or press <kbd className="bg-white/20 px-1 rounded text-xs">Space</kbd> to select points
              </p>
              <p className="text-xs mt-1 opacity-90">
                First click = Point 1 • Second click = Calculate distance
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Measurement Styles */}
      <style jsx>{`
        .custom-measurement-hotspot {
          background-color: #ff4444 !important;
          width: 12px !important;
          height: 12px !important;
          border-radius: 50% !important;
          border: 2px solid white !important;
          box-shadow: 0 0 5px rgba(0,0,0,0.5) !important;
          z-index: 90 !important;
        }
        .distance-measurement-hotspot {
          background-color: rgba(0, 0, 0, 0.8) !important;
          color: white !important;
          padding: 4px 8px !important;
          border-radius: 4px !important;
          font-size: 12px !important;
          font-weight: bold !important;
          white-space: nowrap !important;
          border: 1px solid white !important;
          z-index: 90 !important;
        }
        .pnlm-hotspot-base {
          transition: all 0.3s ease !important;
        }
        .pnlm-hotspot-base:hover {
          transform: scale(1.1) !important;
        }
      `}</style>
    </>
  );
};

export default MeasurementUI;
